/**
 * Wishlist Manager for Horizon Theme
 * Uses localStorage to persist wishlist across pages.
 * Product handles are stored in the 'horizon_wishlist' key.
 */

const WISHLIST_KEY = 'horizon_wishlist';

const Wishlist = {
  /**
   * Get all wishlisted product handles
   * @returns {string[]}
   */
  getItems() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch (e) {
      return [];
    }
  },

  /**
   * Check if a product handle is in the wishlist
   * @param {string} handle
   * @returns {boolean}
   */
  hasItem(handle) {
    return this.getItems().includes(handle);
  },

  /**
   * Add a product handle to the wishlist
   * @param {string} handle
   */
  addItem(handle) {
    const items = this.getItems();
    if (!items.includes(handle)) {
      items.push(handle);
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      this._dispatchChange(handle, 'added');
    }
  },

  /**
   * Remove a product handle from the wishlist
   * @param {string} handle
   */
  removeItem(handle) {
    const items = this.getItems().filter((h) => h !== handle);
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    this._dispatchChange(handle, 'removed');
  },

  /**
   * Toggle a product handle in the wishlist
   * @param {string} handle
   * @returns {boolean} true if added, false if removed
   */
  toggleItem(handle) {
    if (this.hasItem(handle)) {
      this.removeItem(handle);
      return false;
    } else {
      this.addItem(handle);
      return true;
    }
  },

  /**
   * Get the count of items in the wishlist
   * @returns {number}
   */
  getCount() {
    return this.getItems().length;
  },

  /**
   * Clear all items from the wishlist
   */
  clearAll() {
    localStorage.removeItem(WISHLIST_KEY);
    this._dispatchChange(null, 'cleared');
  },

  /**
   * Dispatch a custom event when wishlist changes
   * @param {string} handle
   * @param {string} action
   */
  _dispatchChange(handle, action) {
    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        detail: { handle, action, count: this.getCount() },
      })
    );
  },
};

/**
 * Initialize all wishlist buttons on the page
 */
function initWishlistButtons() {
  const buttons = document.querySelectorAll('[data-wishlist-button]');
  buttons.forEach((btn) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;

    // Set initial active state
    updateButtonState(btn, Wishlist.hasItem(handle));

    // Attach click handler (avoid duplicate listeners)
    if (!btn.dataset.wishlistInitialized) {
      btn.dataset.wishlistInitialized = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const added = Wishlist.toggleItem(handle);
        updateButtonState(btn, added);

        // Visual feedback animation
        btn.classList.add('wishlist-btn--animate');
        btn.addEventListener('animationend', () => {
          btn.classList.remove('wishlist-btn--animate');
        }, { once: true });
      });
    }
  });
}

/**
 * Update the visual state of a wishlist button
 * @param {HTMLElement} btn
 * @param {boolean} isActive
 */
function updateButtonState(btn, isActive) {
  if (isActive) {
    btn.classList.add('wishlist-btn--active');
    btn.setAttribute('aria-label', btn.dataset.labelRemove || 'Remove from wishlist');
    btn.setAttribute('aria-pressed', 'true');
  } else {
    btn.classList.remove('wishlist-btn--active');
    btn.setAttribute('aria-label', btn.dataset.labelAdd || 'Add to wishlist');
    btn.setAttribute('aria-pressed', 'false');
  }
}

/**
 * Update all wishlist badge/count elements
 */
function updateWishlistCount() {
  const countEls = document.querySelectorAll('[data-wishlist-count]');
  const count = Wishlist.getCount();
  countEls.forEach((el) => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

// Listen for wishlist changes to update counts and page products
document.addEventListener('wishlist:change', () => {
  updateWishlistCount();
  // If on the wishlist page, re-render products
  if (document.getElementById('wishlist-page-products')) {
    renderWishlistPage();
  }
});

/**
 * Render product cards on the Wishlist page using the Shopify AJAX API
 */
async function renderWishlistPage() {
  const container = document.getElementById('wishlist-page-products');
  const emptyState = document.getElementById('wishlist-empty-state');
  const clearAllBtn = document.getElementById('wishlist-clear-all');
  if (!container) return;

  const handles = Wishlist.getItems();

  if (handles.length === 0) {
    container.innerHTML = '';
    if (emptyState) emptyState.style.display = 'block';
    if (clearAllBtn) clearAllBtn.style.display = 'none';
    return;
  }

  if (emptyState) emptyState.style.display = 'none';
  if (clearAllBtn) clearAllBtn.style.display = 'inline-flex';

  // Show loading state
  container.innerHTML = `<div class="wishlist-loading"><div class="wishlist-spinner"></div></div>`;

  // Fetch all products in parallel
  const productPromises = handles.map((handle) =>
    fetch(`/products/${handle}.js`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
  );

  const products = (await Promise.all(productPromises)).filter(Boolean);

  if (products.length === 0) {
    container.innerHTML = '';
    if (emptyState) emptyState.style.display = 'block';
    return;
  }

  container.innerHTML = products
    .map((product) => buildProductCard(product))
    .join('');

  // Re-initialize wishlist buttons in the newly rendered cards
  initWishlistButtons();

  // Initialize explicit remove buttons
  container.querySelectorAll('[data-wishlist-remove]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      Wishlist.removeItem(btn.dataset.wishlistRemove);
    });
  });
  
  // Initialize clear all button
  if (clearAllBtn && !clearAllBtn.dataset.initialized) {
    clearAllBtn.dataset.initialized = 'true';
    clearAllBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (confirm('Are you sure you want to clear all items from your wishlist?')) {
        Wishlist.clearAll();
      }
    });
  }
}

/**
 * Build an HTML product card for the wishlist page
 * @param {object} product - Shopify product JSON
 * @returns {string} HTML string
 */
function buildProductCard(product) {
  const variant = product.variants[0];
  const price = formatMoney(variant.price);
  const comparePriceRaw = variant.compare_at_price;
  const comparePrice = comparePriceRaw && comparePriceRaw > variant.price
    ? formatMoney(comparePriceRaw)
    : null;
    
  let discountInline = '';
  if (comparePriceRaw && comparePriceRaw > variant.price) {
    const discount = Math.round(((comparePriceRaw - variant.price) / comparePriceRaw) * 100);
    const arrowSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="wishlist-discount-arrow"><line x1="12" y1="4" x2="12" y2="20"></line><polyline points="18 14 12 20 6 14"></polyline></svg>`;
    discountInline = `<span class="wishlist-price--discount">${arrowSvg} ${discount}%</span>`;
  }

  const image = product.featured_image || '';
  const isActive = Wishlist.hasItem(product.handle);
  
  const settings = window.WishlistCardSettings || { imageRatio: 'square', showVendor: false };
  const ratioClass = `wishlist-media--${settings.imageRatio}`;
  const vendorHtml = settings.showVendor ? `<div class="wishlist-product-card__vendor">${product.vendor}</div>` : '';

  return `
    <div class="wishlist-product-card" data-product-handle="${product.handle}">
      <div class="wishlist-product-card__media">
        <a href="/products/${product.handle}">
          ${image
            ? `<img src="${image}" alt="${product.title}" loading="lazy" class="${ratioClass}" width="400" height="400">`
            : `<div class="wishlist-product-card__placeholder ${ratioClass}"></div>`
          }
        </a>
        <button
          type="button"
          class="wishlist-remove-btn-overlay"
          data-wishlist-remove="${product.handle}"
          aria-label="Remove from wishlist"
          title="Remove"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
      <div class="wishlist-product-card__info">
        ${vendorHtml}
        <a href="/products/${product.handle}" class="wishlist-product-card__title">
          ${product.title}
        </a>
        <div class="wishlist-product-card__price">
          ${discountInline}
          <span class="wishlist-price--current">${price}</span>
          ${comparePrice ? `<s class="wishlist-price--compare">${comparePrice}</s>` : ''}
        </div>
        <div class="wishlist-product-card__actions">
          <a href="/products/${product.handle}" class="wishlist-btn-cart button">
            View Product
          </a>
        </div>
      </div>
    </div>
  `;
}

/**
 * Format money in rupees / store currency
 * @param {number} cents - Amount in cents (or paisa)
 * @returns {string}
 */
function formatMoney(cents) {
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    let money = Shopify.formatMoney(cents, '{{amount}}');
    return money.replace(/\.00$/, '');
  }
  const amount = (cents / 100);
  return `₹${amount % 1 === 0 ? amount : amount.toFixed(2)}`;
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  initWishlistButtons();
  updateWishlistCount();

  // If we are on the wishlist page, render products
  if (document.getElementById('wishlist-page-products')) {
    renderWishlistPage();
  }

  // Observe DOM for dynamically loaded products (e.g. Recommended Products)
  const observer = new MutationObserver((mutations) => {
    const hasNewNodes = mutations.some(m => m.addedNodes.length > 0);
    if (hasNewNodes) {
      clearTimeout(window._wishlistInitTimeout);
      window._wishlistInitTimeout = setTimeout(() => {
        initWishlistButtons();
      }, 150);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
});

// Also init when Shopify section renders (theme editor)
document.addEventListener('shopify:section:load', () => {
  initWishlistButtons();
  updateWishlistCount();
});

// Export for use in other modules if needed
window.Wishlist = Wishlist;
